import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, FileDown, FileText, Plus, X } from 'lucide-react';
import { employeeAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const ZONES = ['1A', '1B', '2', '3', '4', '5', '6'];

// Mois précédent par défaut : la déclaration au comptable se fait après coup,
// une fois le mois terminé.
const moisPrecedent = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const nombre = (v) => (v === '' || v === null || v === undefined ? 0 : Number(v));

// Salarié déclaré en heures de présence (poste adapté) : ni jours par zone, ni panier — ses heures du mois.
const enHeures = (ligne) => ligne.heuresPresence !== null && ligne.heuresPresence !== undefined;

// Fenêtre de relecture avant envoi au comptable : le calcul (absences+motif,
// formations, zones de trajet, paniers) est fait côté serveur, mais CE QUI EST
// TÉLÉCHARGÉ est ce qui est affiché ici — modifiable avant de générer le fichier,
// pas un recalcul silencieux au moment du téléchargement.
const PayrollDeclarationDialog = ({ isOpen, onOpenChange }) => {
  const { toast } = useToast();
  const [mois, setMois] = useState(moisPrecedent);
  const [chargement, setChargement] = useState(false);
  const [lignes, setLignes] = useState(null);
  const [generation, setGeneration] = useState('');

  const charger = async (m) => {
    setChargement(true);
    setLignes(null);
    try {
      const data = await employeeAPI.payrollExportPreview(m);
      setLignes(data.lignes);
    } catch (e) {
      toast({ title: 'Calcul impossible', description: e.response?.data?.detail || String(e),
              variant: 'destructive' });
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => {
    if (isOpen) charger(mois);
    // eslint-disable-next-line
  }, [isOpen]);

  const changerMois = (m) => {
    setMois(m);
    charger(m);
  };

  const majLigne = (employeeId, champs) => {
    setLignes((prev) => prev.map((l) => (l.employeeId === employeeId ? { ...l, ...champs } : l)));
  };

  const majZone = (employeeId, zone, valeur) => {
    setLignes((prev) => prev.map((l) => (
      l.employeeId === employeeId
        ? { ...l, joursParZone: { ...l.joursParZone, [zone]: nombre(valeur) } }
        : l
    )));
  };

  const ajouterAbsence = (employeeId) => {
    setLignes((prev) => prev.map((l) => (
      l.employeeId === employeeId
        ? { ...l, absences: [...l.absences, { debut: `${mois}-01`, fin: `${mois}-01`, motif: '' }] }
        : l
    )));
  };

  const majAbsence = (employeeId, index, champs) => {
    setLignes((prev) => prev.map((l) => (
      l.employeeId === employeeId
        ? { ...l, absences: l.absences.map((a, i) => (i === index ? { ...a, ...champs } : a)) }
        : l
    )));
  };

  const supprimerAbsence = (employeeId, index) => {
    setLignes((prev) => prev.map((l) => (
      l.employeeId === employeeId
        ? { ...l, absences: l.absences.filter((_, i) => i !== index) }
        : l
    )));
  };

  const ajouterFormation = (employeeId) => {
    setLignes((prev) => prev.map((l) => (
      l.employeeId === employeeId
        ? { ...l, formations: [...l.formations, { debut: `${mois}-01`, fin: `${mois}-01`, jours: 1 }] }
        : l
    )));
  };

  const majFormation = (employeeId, index, champs) => {
    setLignes((prev) => prev.map((l) => (
      l.employeeId === employeeId
        ? { ...l, formations: l.formations.map((f, i) => (i === index ? { ...f, ...champs } : f)) }
        : l
    )));
  };

  const supprimerFormation = (employeeId, index) => {
    setLignes((prev) => prev.map((l) => (
      l.employeeId === employeeId
        ? { ...l, formations: l.formations.filter((_, i) => i !== index) }
        : l
    )));
  };

  const telecharger = async (format) => {
    setGeneration(format);
    try {
      const declaration = { mois, lignes };
      const blob = format === 'pdf'
        ? await employeeAPI.payrollExportPdf(declaration)
        : await employeeAPI.payrollExportCsv(declaration);
      const url = URL.createObjectURL(blob);
      const lien = document.createElement('a');
      lien.href = url;
      lien.download = `declaration-heures-${mois}.${format}`;
      document.body.appendChild(lien);
      lien.click();
      lien.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast({ title: 'Génération impossible', description: e.response?.data?.detail || String(e),
              variant: 'destructive' });
    } finally {
      setGeneration('');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-white border border-gray-200">
        <DialogHeader>
          <DialogTitle className="flex items-center text-blue-800">
            <FileText className="w-5 h-5 mr-2" /> Déclaration des heures au comptable
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600">Mois</label>
          <Input type="month" value={mois} onChange={(e) => changerMois(e.target.value)}
                 className="w-40 border-gray-200" />
          {chargement && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
        </div>

        {lignes && !chargement && (
          <>
            <p className="text-xs text-gray-500">
              Relis et corrige si besoin avant de télécharger — rien n'est enregistré dans l'app,
              seul le fichier généré tient compte de tes modifications.
            </p>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b bg-gray-50">
                    <th className="py-2 px-3">Salarié</th>
                    <th className="py-2 px-3">Absences (du / au / motif)</th>
                    <th className="py-2 px-3">Formation (du / au / jours)</th>
                    <th className="py-2 px-2 text-center whitespace-nowrap">Heures de présence</th>
                    {ZONES.map((z) => (
                      <th key={z} className="py-2 px-1 text-center whitespace-nowrap">Zone {z}</th>
                    ))}
                    <th className="py-2 px-1 text-center">Zone ?</th>
                    <th className="py-2 px-2 text-center">Panier (j.)</th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l) => (
                    <tr key={l.employeeId} className="border-b last:border-0 align-top">
                      <td className="py-2 px-3 font-medium text-gray-800 whitespace-nowrap">{l.nom}</td>
                      <td className="py-2 px-3 space-y-1 min-w-[300px]">
                        {l.absences.map((a, i) => (
                          <div key={i} className="flex items-center gap-1">
                            <Input type="date" value={a.debut}
                                   onChange={(e) => majAbsence(l.employeeId, i, { debut: e.target.value })}
                                   className="h-7 text-xs w-32 border-gray-200" />
                            <span className="text-xs text-gray-400">au</span>
                            <Input type="date" value={a.fin}
                                   onChange={(e) => majAbsence(l.employeeId, i, { fin: e.target.value })}
                                   className="h-7 text-xs w-32 border-gray-200" />
                            <Input value={a.motif} placeholder="motif"
                                   onChange={(e) => majAbsence(l.employeeId, i, { motif: e.target.value })}
                                   className="h-7 text-xs border-gray-200" />
                            <button onClick={() => supprimerAbsence(l.employeeId, i)}
                                    className="text-red-500 hover:text-red-700 shrink-0">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                        <button onClick={() => ajouterAbsence(l.employeeId)}
                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                          <Plus className="w-3 h-3" /> Ajouter une absence
                        </button>
                      </td>
                      <td className="py-2 px-3 space-y-1 min-w-[260px]">
                        {l.formations.map((f, i) => (
                          <div key={i} className="flex items-center gap-1">
                            <Input type="date" value={f.debut}
                                   onChange={(e) => majFormation(l.employeeId, i, { debut: e.target.value })}
                                   className="h-7 text-xs w-32 border-gray-200" />
                            <span className="text-xs text-gray-400">au</span>
                            <Input type="date" value={f.fin}
                                   onChange={(e) => majFormation(l.employeeId, i, { fin: e.target.value })}
                                   className="h-7 text-xs w-32 border-gray-200" />
                            <Input type="number" min="0" value={f.jours} placeholder="j."
                                   onChange={(e) => majFormation(l.employeeId, i, { jours: nombre(e.target.value) })}
                                   className="h-7 w-16 text-center text-xs border-gray-200" />
                            <button onClick={() => supprimerFormation(l.employeeId, i)}
                                    className="text-red-500 hover:text-red-700 shrink-0">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                        <button onClick={() => ajouterFormation(l.employeeId)}
                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                          <Plus className="w-3 h-3" /> Ajouter une formation
                        </button>
                      </td>
                      <td className="py-2 px-2 text-center">
                        {enHeures(l) ? (
                          <Input type="number" min="0" step="0.5" value={l.heuresPresence}
                                 onChange={(e) => majLigne(l.employeeId, { heuresPresence: nombre(e.target.value) })}
                                 className="h-7 w-20 text-center border-gray-200" />
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      {ZONES.map((z) => (
                        <td key={z} className="py-2 px-1 text-center">
                          {enHeures(l) ? <span className="text-gray-300">—</span> : (
                            <Input type="number" min="0" value={l.joursParZone[z] || 0}
                                   onChange={(e) => majZone(l.employeeId, z, e.target.value)}
                                   className="h-7 w-16 text-center border-gray-200" />
                          )}
                        </td>
                      ))}
                      <td className="py-2 px-1 text-center">
                        {enHeures(l) ? <span className="text-gray-300">—</span> : (
                          <Input type="number" min="0" value={l.joursZoneInconnue}
                                 onChange={(e) => majLigne(l.employeeId, { joursZoneInconnue: nombre(e.target.value) })}
                                 className="h-7 w-16 text-center border-gray-200" />
                        )}
                      </td>
                      <td className="py-2 px-2 text-center">
                        {enHeures(l) ? <span className="text-gray-300">—</span> : (
                          <Input type="number" min="0" value={l.joursPanier}
                                 onChange={(e) => majLigne(l.employeeId, { joursPanier: nombre(e.target.value) })}
                                 className="h-7 w-16 text-center border-gray-200" />
                        )}
                      </td>
                    </tr>
                  ))}
                  {lignes.length === 0 && (
                    <tr><td colSpan={12} className="py-6 text-center text-gray-400">Aucune donnée pour ce mois.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => telecharger('pdf')} disabled={!!generation}>
                {generation === 'pdf' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Télécharger en PDF
              </Button>
              <Button onClick={() => telecharger('csv')} disabled={!!generation}
                      className="bg-blue-600 hover:bg-blue-700 text-white">
                {generation === 'csv' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                Télécharger en CSV
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PayrollDeclarationDialog;
